// ---- Pure cooking sim (plan 18; pan minigame rework plan 25) ------------------
// The pan-minigame judge + the brewing soak stepper, extracted the same way
// src/sim/fishing.ts was: the game server ticks/judges these, and the studio
// Skill-Sim cooking driver imports the LITERAL same rules. Same purity rule as
// combat.ts/fishing.ts — no index.ts / src/server/* imports, no document/
// window/Bun.*/process.env. rng stays OUTSIDE (the pattern SEED and the
// brew-haul roll are resolved by the caller), so a parity test stays
// deterministic.
//
// The pan minigame is the reel minigame's TWIN over the shared sim/lane.ts
// prompt-lane machinery — the meter reads as BURN instead of tension, and the
// difficulty model is the ingredient's cookTier (the fish tier/fightMul's
// sibling). Cooking is HEARTH-GATED (plan 25): a cook starts only within
// hearthRange of a kitchen range deco or a live player-started campfire.
//
// This module is also the ONE home for the cooking-economy DEFAULTS (the
// meaning of each number + the values the deterministic tests pin). The LIVE
// values are content: content/tunables/cooking.json overrides these
// field-by-field via src/tunables.ts (COOKING_TUNING) — the server handlers,
// the client HUD, and the studio Skill-Sim all read THAT, never these constants
// directly (and never re-declare one locally). The studio's Apply → JSON writes
// the doc, which is how tuned pacing ships to the game.

import { applyLaneResult, forceResolveLane, genLanePattern, startLaneJudge } from "./lane";
import type { LaneEvent, LaneFx, LaneJudge, LanePattern } from "./lane";

// ---- Tuning DEFAULTS shared by both sides -------------------------------------
export const HEARTH_RANGE = 4;        // max reach from a hearth (range/campfire) to start a cook (xz units)
export const COOK_RATE_CD = 0.6;      // seconds between accepted cook starts (rate gate)
export const BREW_SOAK = 420;         // seconds a fermenter soaks before it's ready (× box soakMul)
export const BREW_CAPACITY_MIN = 3;   // units per brew haul (uniform roll, rng outside)
export const BREW_CAPACITY_MAX = 6;
export const BREW_COUNT = 1;          // deployed fermenters allowed at once (+ box brewCount)
export const BREW_RANGE = 7;          // max reach to deploy / collect a fermenter (xz units)

// The pan minigame (plan 25 — replaces the countdown+pull machine):
export const PAN_ARROWS_BASE = 4;     // prompts in the pattern at cook tier 0
export const PAN_ARROWS_PER_TIER = 2; // + prompts per cook tier
export const PAN_GAP_BASE = 0.9;      // s between prompts at tier 0
export const PAN_GAP_MIN = 0.35;      // hard gap floor (playability)
export const PAN_SPEED_RAMP = 0.25;   // gap divisor grows 1 + tier×this
export const PAN_HIT_WINDOW = 0.35;   // ± s a press counts as a hit (× box panWindowMul)
export const PAN_MISS_BURN = 0.34;    // burn added per missed prompt (1.0 = burnt; ≈3 misses)
export const PAN_HIT_RELIEF = 0.15;   // burn shed per hit prompt
export const PAN_INPUT_SLACK = 1.5;   // s the server tolerates a panInput arriving late
export const PAN_DEADLINE_PAD = 3;    // s past the pattern's end before the server force-resolves

// Campfires (the player-started hearth):
export const FIRE_BURN_SECONDS = 600; // s a started campfire burns before going out
export const FIRE_COUNT = 1;          // live campfires allowed per character
export const FIRE_RANGE = 3;          // max reach to start a campfire (xz units)

/** The one item a lost pan minigame mints (no XP, no standing — just charcoal). */
export const BURNT_MESS = "burnt-mess";

/** Pan-minigame difficulty tier per raw ingredient (0..3 — scales prompt count
 *  + pace like a fish's tier scales the reel). Unlisted uses COOK_TIER_DEFAULT. */
export const COOK_TIER: Record<string, number> = {
  minnow: 0, perch: 1, "raw-meat": 1, trout: 2, venison: 3, pike: 3,
};
export const COOK_TIER_DEFAULT = 1;

/** Cooking XP per dish, by raw ingredient (~8-12 avg → ~10 dishes to 100). */
export const COOK_XP: Record<string, number> = {
  minnow: 5, perch: 10, trout: 20, pike: 40, "raw-meat": 12, venison: 24,
};
export const COOK_XP_DEFAULT = 8;
/** Brewing XP per unit landed in a haul (a 3-6 haul → 12-24). */
export const BREWING_XP_PER_UNIT = 4;
/** Cooks' Guild standing deeds: both branches feed the same patron. */
export const COOK_PER_DISH = 10;
export const COOK_PER_BREW = 15;

// ---- The pan minigame (plan 25) -----------------------------------------------
// Cooking's skill check is a prompt-lane pattern (FLIP/STIR/SEASON/TOSS on
// WASD) over the shared sim/lane.ts machinery — fishing's reel twin with the
// meter read as BURN instead of tension. Pattern generation is SEED-
// deterministic: the server rolls one seed, sends it in `panOn`, and both
// sides expand the identical pattern. The judge's "landed" = cooked,
// "escaped" = burnt (the caller mints the Burnt Mess). Difficulty scales with
// the ingredient's cook tier the way the reel scales with a fish's tier.

/** The subset of COOKING_TUNING the pan machine reads (structural — callers
 *  pass COOKING_TUNING / a sandbox copy straight in). */
export type PanTuning = {
  panArrowsBase: number;
  panArrowsPerTier: number;
  panGapBase: number;
  panGapMin: number;
  panSpeedRamp: number;
  panMissBurn: number;
  panHitRelief: number;
};

/** Judge/pattern/event shapes are the shared lane types — burn IS the lane
 *  meter (`judge.tension`), "landed"|"escaped" map to cooked|burnt. */
export type PanJudge = LaneJudge;
export type PanPattern = LanePattern;
export type PanEvent = LaneEvent;

/** Build the prompt pattern for one dish. Count + pace scale with the
 *  ingredient's cook tier; never below panGapMin; ±20% gap jitter; no
 *  immediate same-lane repeats (all inherited from genLanePattern). */
export function genPanPattern(seed: number, tier: number, tun: PanTuning): PanPattern {
  const count = Math.max(3, Math.round(tun.panArrowsBase + tier * tun.panArrowsPerTier));
  const gap = tun.panGapBase / (1 + tier * tun.panSpeedRamp);
  return genLanePattern(seed, count, gap, tun.panGapMin);
}

/** The lane-judge meter arithmetic for the pan: miss chars, hit relieves. */
export function panFx(tun: PanTuning): LaneFx {
  return { miss: tun.panMissBurn, relief: tun.panHitRelief };
}

export function startPanJudge(pattern: PanPattern, hitWindow: number): PanJudge {
  return startLaneJudge(pattern, hitWindow);
}

/** Judge the next prompt. Burns the moment the meter reaches 1 (mid-pattern);
 *  cooks when the last prompt resolves with the meter still under it. */
export function applyPanResult(j: PanJudge, hit: boolean, tun: PanTuning): PanEvent[] {
  return applyLaneResult(j, hit, panFx(tun));
}

/** Deadline resolution (AFK / stalled input): every unjudged prompt is a miss. */
export function forcePanResolve(j: PanJudge, tun: PanTuning): PanEvent[] {
  return forceResolveLane(j, panFx(tun));
}

// ---- Brewing soak stepper -------------------------------------------------------
// The passive branch: a fermenter (ale/preserves) soaks, then yields a bulk
// haul. The server will key deployed fermenters on ABSOLUTE epoch `readyAt`
// (they persist across downtime, like fishing nets / hunting traps / NPC
// respawns) — this dt-stepper is the studio/test half of the same rule, for
// arenas with no wall clock. Its own names (not fishing's startSoak/stepNet)
// keep the domain self-contained, matching hunting's startTrapSoak/stepTrap.

export type BrewState = {
  /** Seconds of soak left (0 = ready to collect). */
  soakLeft: number;
};

export type BrewEvent = { kind: "brewReady" };

const BREW_READY: BrewEvent[] = [{ kind: "brewReady" }];

export function startBrew(soakTime: number): BrewState {
  return { soakLeft: soakTime };
}

/** Count a fermenter's soak down; emits `brewReady` exactly once, on the crossing. */
export function stepBrew(s: BrewState, dt: number): BrewEvent[] {
  if (s.soakLeft <= 0) return [];
  s.soakLeft -= dt;
  return s.soakLeft <= 0 ? BREW_READY : [];
}
